import { BusinessSpecialty, GroupedServices, Salon, Service } from "@/types/booking";

// Nigerian beauty service keywords mapping based on customer search patterns
const SERVICE_KEYWORDS: Record<string, string[]> = {
  // Facial services - high priority keywords
  facial: ['facials', 'facial treatment', 'facial spa', 'book facial', 'best facial spa'],
  'deep cleansing': ['deep cleansing facial', 'facial cleansing'],
  'hydrating facial': ['hydrating facial', 'moisture facial'],
  'anti-aging': ['anti-aging facial', 'anti-aging treatment'],
  'facial peel': ['facial peels', 'chemical peel', 'skin peel'],
  
  // Nail services - high priority keywords
  manicure: ['manicure', 'book manicure', 'nail care'],
  pedicure: ['pedicure', 'book pedicure', 'foot care'],
  'gel': ['gel nails', 'gel manicure', 'gel polish'],
  'gel nail': ['gel nails', 'gel manicure', 'gel polish'],
  'nail': ['nail salon', 'best nail salon', 'nails'],
  'acrylic': ['acrylic nails', 'nail extensions'],
  'nail art': ['nail art', 'nail design'],
  
  // Hair services - high priority keywords
  hair: ['hair salon', 'best hair salon', 'hair styling'],
  'natural hair': ['natural hair salon', 'natural hair stylist', 'best natural hair stylist', 'natural hair care'],
  braids: ['braids', 'book braids', 'hair braiding', 'braid stylist'],
  'box braids': ['box braids', 'protective styling'],
  weave: ['weave installation', 'hair weave', 'sew-in weave'],
  relaxer: ['hair relaxer', 'hair relaxing', 'chemical treatment'],
  color: ['hair color', 'hair coloring', 'hair dye'],
  
  // Waxing services - high priority keywords
  waxing: ['waxing', 'waxing salon', 'book waxing', 'hair removal'],
  'brazilian': ['brazilian wax', 'brazilian waxing', 'bikini wax'],
  'leg wax': ['leg waxing', 'full leg wax'],
  'eyebrow': ['eyebrow waxing', 'brow wax', 'eyebrow shaping'],
  
  // Makeup services - high priority keywords
  makeup: ['makeup', 'makeup artist', 'book makeup'],
  'bridal': ['bridal makeup', 'wedding makeup'],
  'bridal makeup': ['bridal makeup', 'wedding makeup'],
  'party makeup': ['party makeup', 'event makeup'],
  
  // Spa & Massage - high priority keywords
  massage: ['massage', 'massage therapist', 'book massage', 'spa massage'],
  'swedish massage': ['swedish massage', 'relaxation massage'],
  'deep tissue': ['deep tissue massage', 'therapeutic massage'],
  spa: ['spa', 'best spa', 'spa treatment', 'wellness spa'],
  
  // Skincare - high priority keywords
  skincare: ['skincare', 'skincare treatment', 'skin treatment'],
  'chemical peel': ['chemical peel', 'facial peels', 'skin peel'],
  microneedling: ['microneedling', 'skin needling'],
  'acne': ['acne treatment', 'acne treatment spa', 'acne facial'],
  'acne treatment': ['acne treatment', 'acne treatment spa', 'acne facial'],
  
  // Lashes & Brows
  lashes: ['eyelash extensions', 'lash extensions', 'false lashes'],
  'lash lift': ['lash lift', 'lash perm'],
  microblading: ['microblading', 'microblading eyebrows', 'eyebrow microblading'],
  'brow tint': ['eyebrow tinting', 'brow tint']
};

// Extract service categories from service names and actual categories
export const extractServiceCategories = (services: Service[]): string[] => {
  const categories = new Set<string>();
  
  services.forEach(service => {
    // Always add the original service name
    if (service.name) {
      categories.add(service.name.toLowerCase());
    }
    
    // Always add the actual service category if it exists
    if (service.category?.name) {
      categories.add(service.category.name.toLowerCase());
    }
    
    // Add keyword mappings as additional SEO terms (not as a limitation)
    const serviceName = service.name?.toLowerCase() || '';
    Object.entries(SERVICE_KEYWORDS).forEach(([key, keywords]) => {
      if (serviceName.includes(key)) {
        keywords.forEach(keyword => categories.add(keyword));
      }
    });
    
    // Add common variations for categories (as additional terms, not replacements)
    if (service.category?.name) {
      const categoryName = service.category.name.toLowerCase();
      if (categoryName.includes('hair')) {
        categories.add('hair salon');
        categories.add('hair care');
      } else if (categoryName.includes('nail')) {
        categories.add('nail salon');
        categories.add('nail care');
      } else if (categoryName.includes('skin') || categoryName.includes('facial')) {
        categories.add('skincare');
        categories.add('facial treatment');
      } else if (categoryName.includes('makeup')) {
        categories.add('makeup artist');
        categories.add('makeup services');
      } else if (categoryName.includes('spa') || categoryName.includes('massage')) {
        categories.add('spa services');
        categories.add('massage therapy');
      }
    }
  });
  
  return Array.from(categories);
};

// Format location for SEO (handles Nigerian location formats)
export const formatLocation = (salon?: Salon | null): {
  city?: string;
  state?: string;
  area?: string;
  fullLocation?: string;
} => {
  if (!salon) return {};
  
  const city = salon.city?.trim();
  const state = salon.state?.trim();
  const address = salon.address?.trim();
  
  // Extract area from address (common Nigerian address patterns)
  let area: string | undefined;
  if (address) {
    // Try to extract area name (usually comes before city/state)
    const addressParts = address.split(',').map(part => part.trim());
    
    // Common Nigerian location patterns to look for
    const commonAreas = ['Lekki', 'VI', 'Victoria Island', 'Ikeja', 'Yaba', 'Ikoyi', 'Ajah', 
                        'Surulere', 'Gbagada', 'Magodo', 'Maryland', 'Anthony', 'Ogudu',
                        'Garki', 'Wuse', 'Maitama', 'Asokoro', 'Gwarinpa', 'Jabi'];
    
    // Check each part of the address for known areas
    for (const part of addressParts) {
      for (const knownArea of commonAreas) {
        if (part.toLowerCase().includes(knownArea.toLowerCase())) {
          area = knownArea;
          break;
        }
      }
      if (area) break;
    }
    
    // If no known area found, try to extract from address pattern
    if (!area && addressParts.length > 2) {
      area = addressParts[addressParts.length - 2];
    } else if (!area && addressParts.length > 0) {
      // Use the first part if it's the only one
      area = addressParts[0];
    }
  }
  
  // Special handling for "VI" -> "Victoria Island"
  if (area === 'VI') {
    area = 'VI Lagos';
  }
  
  // Build full location string
  const locationParts = [area, city, state].filter(Boolean);
  const fullLocation = locationParts.join(', ');
  
  return { city, state, area, fullLocation };
};

// Generate location-based keywords matching Nigerian search patterns
export const generateLocationKeywords = (
  services: GroupedServices[], 
  salon?: Salon | null
): string[] => {
  // Extract all services from grouped services format
  const allServices: Service[] = services.flatMap(group => group.services);
  const serviceCategories = extractServiceCategories(allServices);
  const location = formatLocation(salon);
  const keywords: string[] = [];
  
  if (!location.city && !location.area) return keywords;
  
  // Priority patterns: "service + in + location" and "service + location"
  serviceCategories.forEach(service => {
    // Pattern: "facials in Lekki", "gel nails in Abuja"
    if (location.area) {
      keywords.push(`${service} in ${location.area}`);
      keywords.push(`${service} ${location.area}`);
      
      // Add "book" variant for high-intent searches
      if (['facial', 'manicure', 'pedicure', 'braids', 'waxing', 'makeup', 'massage'].some(term => service.includes(term))) {
        keywords.push(`book ${service} ${location.area}`);
      }
    }
    
    // Pattern: "massage therapist VI Lagos", "nail salon Lagos"
    if (location.city) {
      keywords.push(`${service} in ${location.city}`);
      keywords.push(`${service} ${location.city}`);
      
      // Special pattern for area + city
      if (location.area && location.city) {
        keywords.push(`${service} ${location.area} ${location.city}`);
      }
      
      // Add "book" variant
      if (['facial', 'manicure', 'pedicure', 'braids', 'waxing', 'makeup', 'massage'].some(term => service.includes(term))) {
        keywords.push(`book ${service} ${location.city}`);
      }
    }
    
    // Pattern: "hair salon near me", "waxing near me"
    if (['salon', 'spa', 'waxing', 'nail', 'makeup', 'massage', 'hair', 'facial'].some(term => service.includes(term))) {
      keywords.push(`${service} near me`);
    }
  });
  
  // High-priority location-specific keywords
  if (location.area || location.city) {
    const loc = location.area || location.city;
    
    // Check service types and add specific high-priority keywords
    if (serviceCategories.some(s => s.includes('facial'))) {
      keywords.push(`facials in ${loc}`);
      keywords.push(`book facial ${loc}`);
      keywords.push(`best facial spa ${loc}`);
    }
    
    if (serviceCategories.some(s => s.includes('nail') || s.includes('gel') || s.includes('manicure'))) {
      keywords.push(`gel nails ${loc}`);
      keywords.push(`nail salon ${loc}`);
      keywords.push(`book manicure ${loc}`);
      keywords.push(`best nail salon in ${loc}`);
    }
    
    if (serviceCategories.some(s => s.includes('hair') || s.includes('braid'))) {
      keywords.push(`hair salon in ${loc}`);
      keywords.push(`braid stylist ${loc}`);
      keywords.push(`book braids ${loc}`);
      keywords.push(`best hair salon in ${loc}`);
      if (serviceCategories.some(s => s.includes('natural hair'))) {
        keywords.push(`natural hair salon in ${loc}`);
        keywords.push(`best natural hair stylist ${loc}`);
      }
    }
    
    if (serviceCategories.some(s => s.includes('waxing') || s.includes('brazilian'))) {
      keywords.push(`waxing salon ${loc}`);
      keywords.push(`book waxing ${loc}`);
      if (serviceCategories.some(s => s.includes('brazilian'))) {
        keywords.push(`brazilian wax ${loc}`);
      }
    }
    
    if (serviceCategories.some(s => s.includes('makeup') || s.includes('bridal'))) {
      keywords.push(`makeup artist ${loc}`);
      keywords.push(`book makeup ${loc}`);
      if (serviceCategories.some(s => s.includes('bridal'))) {
        keywords.push(`bridal makeup ${loc}`);
      }
    }
    
    if (serviceCategories.some(s => s.includes('massage') || s.includes('spa'))) {
      keywords.push(`massage therapist ${loc}`);
      keywords.push(`spa massage ${loc}`);
      keywords.push(`book massage ${loc}`);
      keywords.push(`best spa in ${loc}`);
    }
    
    if (serviceCategories.some(s => s.includes('skincare') || s.includes('acne') || s.includes('peel'))) {
      keywords.push(`skincare treatment ${loc}`);
      if (serviceCategories.some(s => s.includes('peel'))) {
        keywords.push(`facial peels ${loc}`);
      }
      if (serviceCategories.some(s => s.includes('acne'))) {
        keywords.push(`acne treatment spa ${loc}`);
      }
    }
    
    if (serviceCategories.some(s => s.includes('microblading'))) {
      keywords.push(`microblading eyebrows ${loc}`);
    }
  }
  
  return Array.from(new Set(keywords)); // Remove duplicates
};

// Generate SEO-friendly title matching Nigerian search patterns
export const generateSeoTitle = (
  businessName: string,
  services: Service[],
  salon?: Salon | null,
  businessSpecialties?: string[] | null
): string => {
  // Build title components
  const components: string[] = [businessName];
  
  // Add business specialties if available
  if (businessSpecialties && businessSpecialties.length > 0) {
    const specialtyDescriptions = businessSpecialties
      .filter(specialty => specialty)
      .map(specialty => specialty)
      .slice(0, 3); // Limit to first 3 specialties to keep title reasonable
    
    if (specialtyDescriptions.length > 0) {
      components.push(specialtyDescriptions.join(' & '));
    }
  }
  
  // Add address if available
  if (salon?.address) {
    components.push(salon.address);
  }
  
  // Add city if available
  if (salon?.city) {
    components.push(salon.city);
  }
  
  // Join components and add Splice branding
  return `${components.join(' - ')} | Splice`;
};

// Generate SEO-friendly description matching Nigerian search patterns
export const generateSeoDescription = (
  businessName: string,
  about: string | null | undefined,
  services: GroupedServices[],
  salon?: Salon | null
): string => {
  const location = formatLocation(salon);
  const allServices: Service[] = services.flatMap(group => group.services);
  const serviceCategories = extractServiceCategories(allServices);
  const loc = location.area || location.city;
  
  // Start with location-based opening
  let description = '';
  
  if (loc) {
    // Determine primary service focus for description
    if (serviceCategories.some(s => s.includes('facial'))) {
      description = `Looking for facials in ${loc}? Book facial treatments at ${businessName}`;
    } else if (serviceCategories.some(s => s.includes('nail') || s.includes('gel') || s.includes('manicure'))) {
      description = `Best nail salon in ${loc}. Book gel nails, manicure, and pedicure at ${businessName}`;
    } else if (serviceCategories.some(s => s.includes('hair') || s.includes('braid'))) {
      if (serviceCategories.some(s => s.includes('natural hair'))) {
        description = `Natural hair salon in ${loc}. Book braids and natural hair styling at ${businessName}`;
      } else {
        description = `Hair salon in ${loc}. Book braids, weaves, and hair treatments at ${businessName}`;
      }
    } else if (serviceCategories.some(s => s.includes('waxing'))) {
      description = `Waxing salon in ${loc}. Book brazilian wax and body waxing at ${businessName}`;
    } else if (serviceCategories.some(s => s.includes('makeup'))) {
      if (serviceCategories.some(s => s.includes('bridal'))) {
        description = `Bridal makeup artist in ${loc}. Book wedding makeup at ${businessName}`;
      } else {
        description = `Professional makeup artist in ${loc}. Book makeup services at ${businessName}`;
      }
    } else if (serviceCategories.some(s => s.includes('massage') || s.includes('spa'))) {
      description = `Best spa in ${loc}. Book massage therapy and spa treatments at ${businessName}`;
    } else if (serviceCategories.some(s => s.includes('skincare'))) {
      description = `Skincare treatment in ${loc}. Book facials and skin treatments at ${businessName}`;
    } else {
      description = `Book professional services at ${businessName} in ${loc}`;
    }
  } else {
    description = `Book appointments online at ${businessName}`;
  }
  
  // Add specific services with location keywords
  const uniqueServices = new Set<string>();
  services.forEach(service => {
    if (service.name) {
      uniqueServices.add(service.name);
    }
  });
  
  const serviceNames = Array.from(uniqueServices).slice(0, 5);
  
  if (serviceNames.length > 0) {
    description += `. We offer ${serviceNames.join(', ')}`;
    if (serviceNames.length > 5) {
      description += ' and more';
    }
  }
  
  // Add business description if available
  if (about && about.length > 20) {
    description += `. ${about.substring(0, 60)}...`;
  }
  
  // Add call-to-action with location
  if (loc) {
    description += ` Easy online booking for ${loc} customers.`;
  } else {
    description += ` Easy online booking available.`;
  }
  
  return description;
};